import React, { useState, useEffect, lazy, Suspense } from 'react';
import Loading from '../Component/Loading';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faHandshake, faPersonCirclePlus } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import SEOHelmet from '../Component/SEOHelmet';
import NoDataFull from './NoDataFull'; // Import the NoDataFull component

const Header = lazy(() => import('../Component/Header'));
const Footer = lazy(() => import('../Component/Footer'));
const Touch = lazy(() => import('../Component/Home/Touch'));
const Position = lazy(() => import('../Component/Home/position'));

const HiringPage = () => {
    const [loading, setLoading] = useState(true);
    const [position, setPosition] = useState();
    const [data, setData] = useState();
    const [isScrollTopVisible, setIsScrollTopVisible] = useState(false);
    const [apiError, setApiError] = useState(false); // Add API error state

    const handleClick = () => {
        window.location.href = '/hiring';
    };

    const getData = () => {
        setLoading(true);
        setApiError(false); // Reset error state
        
        axios.get('https://api.ps-link.world/api/plexus/position/read')
            .then((res) => {
                const data = res.data.data;
                setPosition(data);
                setLoading(false);
                setApiError(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Failed to fetch data.");
                setLoading(false);
                setApiError(true); // Set error state when API fails
            });
    };

    // Check if position has data (similar to hasPortfolioData function)
    const hasPositionData = () => {
        if (!position) return false;
        
        if (Array.isArray(position)) {
            // Count items where status !== false (active items)
            const activeItems = position.filter(item => item.status !== false);
            return activeItems.length > 0;
        }
        
        if (typeof position === 'object') {
            // Check if object has keys
            const keys = Object.keys(position);
            if (keys.length === 0) return false;
            
            // If position has a data array property, check active items in it
            if (position.data && Array.isArray(position.data)) {
                const activeItems = position.data.filter(item => item.status !== false);
                return activeItems.length > 0;
            }
            
            // If position has other array properties, check them too
            if (position.positions && Array.isArray(position.positions)) {
                const activeItems = position.positions.filter(item => item.status !== false);
                return activeItems.length > 0;
            }
            
            // If position has a count/length property, check if it's > 0
            if (position.count !== undefined && position.count === 0) return false;
            if (position.length !== undefined && position.length === 0) return false;
            if (position.total !== undefined && position.total === 0) return false;
            
            return true;
        }
        
        // Check if position is exactly 0
        if (position === 0) return false;
        return true;
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        getData();

        const storedData = sessionStorage.getItem('hiringStatus');
        if (storedData) {
            setData(JSON.parse(storedData)); // Parse the JSON string
        }

        const handleScroll = () => {
            if (window.scrollY > 200) {
                setIsScrollTopVisible(true);
            } else {
                setIsScrollTopVisible(false);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Show NoDataFull only after loading is complete and either API failed or no data exists
    if (!loading && (apiError || !hasPositionData())) {
        return <NoDataFull />;
    }

    return (
        <div>
            <SEOHelmet
                title="Hiring | Plexus Technology"
                description="Join Plexus Technology and be part of a team driving innovation in IT solutions. Explore career opportunities and contribute to the future of technology with us."
                keywords="Careers at Plexus , Job opportunities , IT careers , Plexus jobs , Work with Plexus , Join Plexus , IT positions , Career openings , Job vacancies , Full-time roles , Tech jobs , Plexus employment , Join team Plexus , Hiring now , IT job roles , Plexus hiring , Technology careers , Apply for jobs , IT experts , Join our team , Future of tech , Innovative workforce , Start your journey , Explore opportunities"
                ogTitle="Hiring | Plexus Technology"
                ogDescription="Join Plexus Technology and be part of a team driving innovation in IT solutions. Explore career opportunities and contribute to the future of technology with us."
                ogImage="https://plexus-technology.in/static/media/logo.589291ad7a3760919228.png"
                twitterCard="summary_large_image"
            />

            {loading ? (
                <Loading />
            ) : (
                <Suspense fallback={<Loading />} >
                    <Header />
                    <Position data={position} loading={loading}/>
                    <Touch />
                    <Footer />
                </Suspense>
            )}
            
            <>
                <div className="fixed-button d-flex">
                    <div className="w-auto mx-auto d-flex gap-3 flex-wrap align-items-center justify-content-center">
                        {data === "true" && (
                            <button
                                className="button1"
                                onClick={handleClick}
                            >
                                <FontAwesomeIcon icon={faPersonCirclePlus} className="me-2" />
                                We're Hiring
                            </button>
                        )}

                        <a
                            href="/portfolio"
                            className="button2 text-decoration-none"
                        >
                            <FontAwesomeIcon icon={faHandshake} className="me-2" />
                            Let’s Collaborate
                        </a>
                    </div>
                </div>
            </>

            {isScrollTopVisible && (
                <p className='fixed-top-btn cursor' onClick={scrollToTop}>
                    <FontAwesomeIcon icon={faArrowUp} className='px-2' />
                </p>
            )}
        </div>
    );
};

export default HiringPage;